import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PlanFeaturesScrollProps {
  title: string;
  items: Array<{ key: string; label: string; enabled?: boolean }>;
  emptyLabel?: string;
}

export const PlanFeaturesScroll: React.FC<PlanFeaturesScrollProps> = ({
  title,
  items,
  emptyLabel = 'Sin capacidades',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const enabledCount = items.filter((item) => item.enabled !== false).length;
  // Activas primero, desactivadas al final (orden relativo estable dentro de cada grupo)
  const sortedItems = [...items].sort((a, b) => {
    const aOn = a.enabled !== false;
    const bOn = b.enabled !== false;
    if (aOn === bOn) return 0;
    return aOn ? -1 : 1;
  });

  const headerMeta =
    items.length > 0 ? (
      <span className="text-[10px] tabular-nums text-gray-400">
        {enabledCount}/{items.length}
      </span>
    ) : null;

  const list = (
    <div className="rounded-lg border border-gray-100 bg-slate-50/70 px-2.5 py-2 dark:border-gray-700/80 dark:bg-gray-900/30">
      <ul className="space-y-0.5">
        {sortedItems.map((feature) => {
          const enabled = feature.enabled !== false;

          return (
            <li
              key={feature.key}
              className={`flex items-start gap-2 rounded-md px-1.5 py-0.5 text-sm ${
                enabled
                  ? 'text-gray-800 dark:text-gray-200'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <span
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                  enabled
                    ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300'
                    : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                }`}
              >
                {enabled ? '✓' : '×'}
              </span>
              <span className={`font-medium leading-snug ${enabled ? '' : 'line-through decoration-gray-400/70'}`}>
                {feature.label}
              </span>
            </li>
          );
        })}
        {items.length === 0 && (
          <li className="px-1.5 py-2 text-sm text-gray-400">{emptyLabel}</li>
        )}
      </ul>
    </div>
  );

  return (
    <div>
      {/* Mobile: colapsable */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mb-2 flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left transition hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/80 md:hidden"
        aria-expanded={open}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {title}
          </span>
          {headerMeta}
        </span>
        <span className="flex items-center gap-1 text-xs font-medium text-teal-700 dark:text-teal-300">
          {open ? t('pages.plans.hideCapabilities', { defaultValue: 'Ocultar' }) : t('pages.plans.showCapabilities', { defaultValue: 'Ver' })}
          <svg
            className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      {/* Desktop: título fijo */}
      <div className="mb-1.5 hidden items-center justify-between gap-2 md:flex">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {title}
        </h3>
        {headerMeta}
      </div>

      <div className={`${open ? 'block' : 'hidden'} md:block`}>{list}</div>
    </div>
  );
};
